import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { errorLines, errorMessage, listCategories, listCountries, listTemplates } from '../../api';
import { paths } from '../../app/paths';
import { useAsyncData } from '../../hooks/useAsyncData';
import { Badge, Button, ErrorState, InlineAlert, Modal, Skeleton, TextField, Toggle, cx, useToast } from '../../ui';
import type { Category, TargetGroupTemplate } from '../../types';
import { useAuth } from '../auth/AuthProvider';
import { summarise, templateParts } from '../templates/templateInfo';
import { PartialCreateError, TemplateApplyError, createProjectWithTargetGroup } from './createProjectFlow';
import { SURVEY_URL_EXAMPLE, SURVEY_URL_HINT, surveyUrlError } from './surveyUrl';

type DeviceKey = 'mobiles' | 'desktops' | 'tablets';
interface NewProjectValues {
  name: string;
  devices: Record<DeviceKey, boolean>;
  requiresWebcam: boolean;
  restrictRegion: boolean;
  collectsPII: boolean;
  preventDuplicates: boolean;
  surveyUrl: string;
  contactName: string;
  phone: string;
  email: string;
}
interface DialogErrors { name?: string; devices?: string; categories?: string; email?: string; surveyUrl?: string }

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INITIAL: NewProjectValues = {
  name: '',
  devices: { mobiles: true, desktops: true, tablets: false },
  requiresWebcam: false,
  restrictRegion: false,
  collectsPII: false,
  preventDuplicates: true, // the backend's default too: each respondent counts once
  surveyUrl: '',
  contactName: '',
  phone: '',
  email: '',
};

const STEPS = ['Basic settings', 'Categories'];

function StepIndicator({ step }: { step: number }) {
  return (
    <div className="mb-6" aria-label={`Step ${step} of ${STEPS.length}: ${STEPS[step - 1]}`}>
      <div className="flex gap-2" aria-hidden="true">
        {STEPS.map((label, i) => <div key={label} className={cx('h-1.5 flex-1 rounded-full', i < step ? 'bg-brand-600' : 'bg-chip')} />)}
      </div>
      <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-muted">Step {step} of {STEPS.length}</p>
    </div>
  );
}

/**
 * Two-step "New campaign" dialog:
 *   1. Basic settings: name, device suitability, webcam / region / PII switches.
 *   2. Categories: searchable picker; "Create campaign" lives here only.
 * Values are kept while moving Back and forth. Nothing is sent to the server until "Create campaign".
 */
export function NewProjectDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { notify } = useToast();
  const { user } = useAuth();
  const clientId = user && typeof user.clientId === 'string' && user.clientId ? user.clientId : undefined;
  const categories = useAsyncData<Category[]>((signal) => listCategories({ signal }), [], { enabled: open }); // prefetched so step 2 opens instantly
  // Templates for the first target group: company-wide plus the user's client's own. Prefetched like the categories.
  const templates = useAsyncData<TargetGroupTemplate[]>((signal) => listTemplates(clientId, { signal }), [clientId], { enabled: open });
  const countries = useAsyncData<{ country_id: string | number; name: string }[]>((signal) => listCountries({ signal }), [], { enabled: open });
  const countryNames = useMemo(() => Object.fromEntries((countries.data || []).map((c) => [String(c.country_id), c.name])), [countries.data]);
  const [templateId, setTemplateId] = useState<string | null>(null); // null = start the first target group blank
  const [partial, setPartial] = useState<{ projectId: string; targetGroupId: string } | null>(null); // created, template not applied
  const [step, setStep] = useState(1);
  const [values, setValues] = useState<NewProjectValues>(INITIAL);
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [errors, setErrors] = useState<DialogErrors>({});
  const [formError, setFormError] = useState<string[]>([]);
  const [contactOpen, setContactOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false); // synchronous guard: state updates are async, a fast double click is not

  const reset = () => {
    setStep(1); setValues(INITIAL); setCategoryIds([]); setSearch(''); setErrors({}); setFormError([]); setContactOpen(false); setTemplateId(null); setPartial(null);
  };
  const finishPartial = (openGroup: boolean) => {
    const p = partial;
    reset();
    onClose();
    if (p) navigate(openGroup ? paths.targetGroup(p.projectId, p.targetGroupId, 'basics') : paths.project(p.projectId));
  };
  const close = () => {
    if (partial) { finishPartial(false); return; } // the campaign exists: closing leaves for it
    if (submittingRef.current) return; // do not close mid-request
    reset();
    onClose();
  };

  const visibleCategories = useMemo(() => {
    const all = categories.data || [];
    const q = search.trim().toLowerCase();
    return q ? all.filter((c) => String(c.name).toLowerCase().includes(q)) : all;
  }, [categories.data, search]);

  const toggleCategory = (id: string) =>
    setCategoryIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const setDevice = (key: DeviceKey) => (checked: boolean) => setValues((v) => ({ ...v, devices: { ...v.devices, [key]: checked } }));

  const goNext = (e: React.FormEvent) => {
    e.preventDefault();
    const next: DialogErrors = {};
    if (!values.name.trim()) next.name = 'Enter a campaign name.';
    if (!Object.values(values.devices).some(Boolean)) next.devices = 'Select at least one device type.';
    const badUrl = surveyUrlError(values.surveyUrl);
    if (badUrl) next.surveyUrl = badUrl;
    if (values.email.trim() && !EMAIL_PATTERN.test(values.email.trim())) { next.email = 'Enter a valid email address.'; setContactOpen(true); }
    setErrors(next);
    if (Object.keys(next).length) return;
    setFormError([]);
    setStep(2);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current || partial) return;
    if (!categoryIds.length) { setErrors({ categories: 'Select at least one category.' }); return; }
    setErrors({});
    submittingRef.current = true;
    setSubmitting(true);
    setFormError([]);
    try {
      const { project, targetGroup, fromTemplate } = await createProjectWithTargetGroup(values, categoryIds, templateId);
      notify('Campaign created', { tone: 'ok' });
      submittingRef.current = false;
      reset();
      onClose();
      navigate(paths.targetGroup(project.id, targetGroup.id, 'basics'), { state: { fromTemplate } });
    } catch (err) {
      submittingRef.current = false;
      if (err instanceof TemplateApplyError) {
        // The campaign and an empty first target group exist; the template changed nothing.
        setPartial({ projectId: err.project.id, targetGroupId: err.targetGroup.id });
        setFormError(errorLines(err.cause));
      } else if (err instanceof PartialCreateError) {
        notify('Campaign created, but its setup did not finish. You can continue from the campaign page.', { tone: 'danger', duration: 7000 });
        reset();
        onClose();
        navigate(paths.project(err.project.id));
      } else {
        setFormError(errorLines(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const footer = partial ? (
    <>
      <Button variant="secondary" onClick={() => finishPartial(false)}>Go to the campaign</Button>
      <Button onClick={() => finishPartial(true)}>Open the empty target group</Button>
    </>
  ) : step === 1 ? (
    <>
      <Button variant="secondary" onClick={close}>Cancel</Button>
      <Button type="submit" form="new-project-form">Next: Categories →</Button>
    </>
  ) : (
    <div className="flex w-full items-center justify-between">
      <Button variant="secondary" onClick={() => { setErrors({}); setFormError([]); setStep(1); }} disabled={submitting}>← Back</Button>
      <Button type="submit" form="new-project-form" loading={submitting} disabled={categories.loading || Boolean(categories.error)}>Create campaign</Button>
    </div>
  );

  return (
    <Modal open={open} size="xl" title="New campaign" subtitle={STEPS[step - 1]} onClose={close} footer={footer}>
      <StepIndicator step={step} />
      <form id="new-project-form" onSubmit={step === 1 ? goNext : submit} noValidate>
        {step === 1 && (
          <div className="space-y-7">
            <TextField
              label="Campaign name"
              required
              autoFocus
              value={values.name}
              maxLength={120}
              onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))}
              error={errors.name}
              hint="Shown on every step of the campaign."
            />

            <fieldset>
              <legend className="mb-2 text-sm font-semibold text-ink">Survey is suitable for <span className="text-danger">*</span></legend>
              <div className="grid gap-3 sm:grid-cols-3">
                <Toggle label="Mobile" checked={values.devices.mobiles} onChange={setDevice('mobiles')} />
                <Toggle label="Desktop" checked={values.devices.desktops} onChange={setDevice('desktops')} />
                <Toggle label="Tablet" checked={values.devices.tablets} onChange={setDevice('tablets')} />
              </div>
              {errors.devices && <p className="mt-2 text-xs text-danger" role="alert">{errors.devices}</p>}
            </fieldset>

            <fieldset>
              <legend className="mb-2 text-sm font-semibold text-ink">Respondent settings</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                <Toggle label="Requires webcam" description="Respondents need a camera" checked={values.requiresWebcam} onChange={(c) => setValues((v) => ({ ...v, requiresWebcam: c }))} />
                <Toggle label="Restrict region" description="Limit to chosen regions" checked={values.restrictRegion} onChange={(c) => setValues((v) => ({ ...v, restrictRegion: c }))} />
                <Toggle label="Collects PII" description="Personal information is collected" checked={values.collectsPII} onChange={(c) => setValues((v) => ({ ...v, collectsPII: c }))} />
                <Toggle label="Prevent duplicates" description="Each respondent counts once" checked={values.preventDuplicates} onChange={(c) => setValues((v) => ({ ...v, preventDuplicates: c }))} />
              </div>
            </fieldset>

            <TextField label="Survey URL (optional)" type="url" inputMode="url" placeholder={SURVEY_URL_EXAMPLE} value={values.surveyUrl} maxLength={2000} onChange={(e) => setValues((v) => ({ ...v, surveyUrl: e.target.value }))} error={errors.surveyUrl} hint={SURVEY_URL_HINT} />

            <details open={contactOpen} onToggle={(e) => setContactOpen((e.currentTarget as HTMLDetailsElement).open)} className="rounded-lg border border-line px-4 py-3">
              <summary className="cursor-pointer text-sm font-semibold text-ink">Contact for this campaign <span className="font-normal text-muted">(optional)</span></summary>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <TextField label="Contact name" value={values.contactName} maxLength={120} onChange={(e) => setValues((v) => ({ ...v, contactName: e.target.value }))} />
                <TextField label="Phone" type="tel" value={values.phone} maxLength={40} onChange={(e) => setValues((v) => ({ ...v, phone: e.target.value }))} />
                <TextField label="Email" type="email" value={values.email} maxLength={160} onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))} error={errors.email} />
              </div>
            </details>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <div>
              <h3 className="text-base font-semibold text-ink">Which categories does this campaign belong to?</h3>
              <p className="mt-1 text-sm text-muted">Choose at least one. You can pick several.</p>
            </div>

            {categories.loading && !categories.data ? (
              <div className="flex flex-wrap gap-2.5" aria-busy="true" aria-label="Loading categories">{[96, 120, 88, 140, 104, 112, 92, 128].map((w, i) => <Skeleton key={i} className="h-9" style={{ width: w }} />)}</div>
            ) : categories.error ? (
              <ErrorState title="Could not load categories" message={errorMessage(categories.error)} onRetry={categories.reload} />
            ) : (
              <>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="min-w-0 flex-1"><TextField label="Search categories" type="search" autoFocus placeholder={`Search ${(categories.data || []).length} categories…`} value={search} onChange={(e) => setSearch(e.target.value)} /></div>
                  <div className="flex items-center gap-2 pb-1">
                    <Badge tone={categoryIds.length ? 'brand' : 'neutral'}>{categoryIds.length} selected</Badge>
                    {categoryIds.length > 0 && <Button variant="ghost" size="sm" onClick={() => setCategoryIds([])}>Clear</Button>}
                  </div>
                </div>

                {visibleCategories.length === 0 ? (
                  <p className="rounded-lg bg-canvas px-4 py-8 text-center text-sm text-muted">{(categories.data || []).length === 0 ? 'No categories are available yet.' : `No categories match “${search}”.`}</p>
                ) : (
                  <div role="group" aria-label="Categories" className="flex max-h-[26rem] flex-wrap gap-2.5 overflow-y-auto pr-1">
                    {visibleCategories.map((c) => {
                      const on = categoryIds.includes(c.id);
                      return (
                        <label key={c.id} className={cx('cursor-pointer rounded-full border px-3.5 py-2 text-sm font-medium transition-colors', on ? 'border-brand-600 bg-brand-50 text-brand-800' : 'border-line text-ink hover:bg-chip')}>
                          <input type="checkbox" className="sr-only" checked={on} onChange={() => toggleCategory(c.id)} />
                          {on && <span aria-hidden="true">✓ </span>}{c.name}
                        </label>
                      );
                    })}
                  </div>
                )}
              </>
            )}
            {errors.categories && <p className="text-sm text-danger" role="alert">{errors.categories}</p>}

            {(templates.data || []).length > 0 && (
              <fieldset className="border-t border-line pt-5">
                <legend className="mb-1 text-sm font-semibold text-ink">First target group <span className="font-normal text-muted">(optional)</span></legend>
                <p className="mb-3 text-sm text-muted">Start blank, or fill it in from a template. You can change everything afterwards.</p>
                <div role="radiogroup" aria-label="First target group" className="max-h-56 space-y-2 overflow-y-auto pr-1">
                  <label className={cx('flex cursor-pointer items-start gap-3 rounded-lg border px-3.5 py-2.5 text-sm', templateId === null ? 'border-brand-300 bg-brand-50/50' : 'border-line')}>
                    <input type="radio" name="first-target-group" className="mt-0.5 h-4 w-4 accent-brand-600" checked={templateId === null} onChange={() => setTemplateId(null)} />
                    <span><span className="block font-semibold text-ink">Start blank</span><span className="block text-xs text-muted">Set everything up step by step</span></span>
                  </label>
                  {(templates.data || []).map((t) => {
                    const summary = summarise(t, t.basicSetting ? countryNames[String(t.basicSetting.country_id)] : undefined);
                    const parts = templateParts(t);
                    return (
                      <label key={t.id} className={cx('flex cursor-pointer items-start gap-3 rounded-lg border px-3.5 py-2.5 text-sm', templateId === t.id ? 'border-brand-300 bg-brand-50/50' : 'border-line')}>
                        <input type="radio" name="first-target-group" className="mt-0.5 h-4 w-4 accent-brand-600" checked={templateId === t.id} onChange={() => setTemplateId(t.id)} />
                        <span className="min-w-0">
                          <span className="flex flex-wrap items-center gap-2 font-semibold text-ink">{t.name} <Badge tone={t.clientId == null ? 'neutral' : 'brand'}>{t.clientId == null ? 'Company-wide' : 'My client'}</Badge></span>
                          {t.description && <span className="block text-xs text-muted">{t.description}</span>}
                          {summary && <span className="block text-xs text-muted">{summary}</span>}
                          {parts.length > 0 && <span className="mt-1 flex flex-wrap gap-1.5">{parts.map((p) => <span key={p} className="rounded-full bg-chip px-2 py-0.5 text-[11px] font-semibold text-muted">{p}</span>)}</span>}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            )}
          </div>
        )}

        {partial && <p className="mt-5 text-sm text-muted">The campaign and an empty first target group were created, but the template was not applied.</p>}
        {formError.length > 0 && <div className="mt-3"><InlineAlert>{formError.length === 1 ? formError[0] : <><b>{formError[0]}</b><ul className="ml-4 mt-1 list-disc">{formError.slice(1).map((m) => <li key={m}>{m}</li>)}</ul></>}</InlineAlert></div>}
      </form>
    </Modal>
  );
}
